import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserRole } from '../utils/roleUtils';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [confirmMode, setConfirmMode] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [newPasswordSetup, setNewPasswordSetup] = useState('');
  const [confirmNewPasswordSetup, setConfirmNewPasswordSetup] = useState('');

  const { signIn, resetPassword, confirmPasswordReset, completeNewPasswordChallenge, newPasswordRequired, user, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  // Role-based routing after login
  const MARKETING_EMAILS = ['marketing@bigsystemone.com'];

  const getPostLoginRoute = (loginEmail: string): string => {
    if (MARKETING_EMAILS.includes(loginEmail.toLowerCase())) {
      localStorage.setItem('bso-view-filter', 'marketing_manager');
      return '/recovery';
    }
    return from;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      // Check if new password is required after sign in
      if (newPasswordRequired) {
        console.log('New password required, staying on login page');
        // Don't navigate, let the UI handle the new password flow
      } else {
        navigate(getPostLoginRoute(email), { replace: true });
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('Starting password reset for email:', email);
      await resetPassword(email);
      console.log('Password reset request completed successfully');
      setResetSuccess(true);
      setConfirmMode(true);
      setResetMode(false);
    } catch (err: any) {
      console.error('Password reset error details:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to send reset email';
      if (err.code === 'UserNotFoundException') {
        errorMessage = 'No account found with this email address';
      } else if (err.code === 'LimitExceededException') {
        errorMessage = 'Too many requests. Please try again later';
      } else if (err.code === 'InvalidParameterException') {
        errorMessage = 'Invalid email address format';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset(email, confirmationCode, newPassword);
      setError('');
      setResetSuccess(false);
      setConfirmMode(false);
      setResetMode(false);
      // Reset form
      setConfirmationCode('');
      setNewPassword('');
      setConfirmNewPassword('');
      alert('Password reset successful! Please sign in with your new password.');
    } catch (err: any) {
      console.error('Password confirmation error:', err);
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPasswordSetup !== confirmNewPasswordSetup) {
      setError('Passwords do not match');
      return;
    }

    if (newPasswordSetup.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await completeNewPasswordChallenge(newPasswordSetup);
      // Navigate to dashboard after successful password setup
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error('New password setup error:', err);
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {newPasswordRequired ? 'Set New Password' : resetMode ? 'Reset Password' : confirmMode ? 'Confirm New Password' : 'Sign in to BigSystemOne'}
          </h2>
        </div>

        {/* New Password Required Form */}
        {newPasswordRequired && (
          <form className="mt-8 space-y-6" onSubmit={handleNewPasswordSetup}>
            <div className="rounded-md bg-yellow-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Your administrator has created an account for you. Please set a new password to continue.
                  </h3>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="new-password-setup" className="sr-only">
                  New Password
                </label>
                <input
                  id="new-password-setup"
                  name="newPasswordSetup"
                  type="password"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="New password (min 8 characters)"
                  value={newPasswordSetup}
                  onChange={(e) => setNewPasswordSetup(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="confirm-new-password-setup" className="sr-only">
                  Confirm New Password
                </label>
                <input
                  id="confirm-new-password-setup"
                  name="confirmNewPasswordSetup"
                  type="password"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Confirm new password"
                  value={confirmNewPasswordSetup}
                  onChange={(e) => setConfirmNewPasswordSetup(e.target.value)}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? 'Setting Password...' : 'Set New Password'}
              </button>
            </div>
          </form>
        )}

        {/* Regular Sign In Form */}
        {!resetMode && !confirmMode && !newPasswordRequired && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <input type="hidden" name="remember" value="true" />
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setResetMode(true)}
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                Forgot your password?
              </button>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
        )}

        {/* Password Reset Form */}
        {resetMode && !confirmMode && (
          <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
            <div>
              <label htmlFor="reset-email" className="sr-only">
                Email address
              </label>
              <input
                id="reset-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Reset Email'}
              </button>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setResetMode(false)}
                className="w-full text-center text-sm text-indigo-600 hover:text-indigo-500"
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {/* Password Confirmation Form */}
        {confirmMode && (
          <form className="mt-8 space-y-6" onSubmit={handleConfirmReset}>
            {resetSuccess && (
              <div className="rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Reset email sent! Check your email for the confirmation code.
                    </h3>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="confirmation-code" className="sr-only">
                  Confirmation Code
                </label>
                <input
                  id="confirmation-code"
                  name="confirmationCode"
                  type="text"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Confirmation code from email"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="new-password" className="sr-only">
                  New Password
                </label>
                <input
                  id="new-password"
                  name="newPassword"
                  type="password"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="New password (min 8 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="confirm-new-password" className="sr-only">
                  Confirm New Password
                </label>
                <input
                  id="confirm-new-password"
                  name="confirmNewPassword"
                  type="password"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Confirm new password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>

            <div>
              <button
                type="button"
                onClick={() => {
                  setConfirmMode(false);
                  setResetMode(false);
                  setResetSuccess(false);
                }}
                className="w-full text-center text-sm text-indigo-600 hover:text-indigo-500"
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {/* Error Display */}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}